import * as React from 'react'
import { FC, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { useSelector as useReduxSelector } from 'react-redux'
import { StoreData } from '@user/store/reducers/typings'
import { useAppDispatch } from '@user/store/hooks'
import {
  getGuestUserLeads,
  getGuestUserOpportunities,
} from '@user/store/guestUser/api'
import Typography from '@mui/material/Typography'
import { GuestUserLeadsTable } from './GuestUserLeadsTable'
import { GuestUserOppsTable } from './GuestUserOppsTable'
import { useGetDivisionsOnMount } from '@user/store/divisions'
import { useGetLocationTypesOnMount } from '@user/store/locationTypes'
import {
  useGetAllFunnelStagesOnMount,
  useGetSalesFunnelListOnMount,
} from '@user/store/salesFunnel'
import { useGetSalesTeamListOnMount } from '@user/store/salesTeam'
import { useGetSourcesOnMount } from '@user/store/sources'
import { useGetCampaignsOnMount } from '@user/store/campaigns'
import { useGetCustomFieldsOnMount } from '@user/store/customFields'
import { useGetBranchListOnMount } from '@user/store/branches'
import { useGetTargetPestsOnMount } from '@user/store/targetPests'
import { useGetUserListOnMount } from '@user/store/users'
import { setLeadsSearch, setOppsSearch } from '@user/store/guestUser'
import { CompanyIdentifier } from '@user/features/CompanyIdentifier'

export const GuestUserDashboard: FC = () => {
  const dispatch = useAppDispatch()
  const [activeTab, setActiveTab] = useState(0)

  const divStatus = useGetDivisionsOnMount()
  const locTypeStatus = useGetLocationTypesOnMount()
  const funnelStatus = useGetSalesFunnelListOnMount()
  const stageStatus = useGetAllFunnelStagesOnMount()
  const teamStatus = useGetSalesTeamListOnMount()
  const sourceStatus = useGetSourcesOnMount()
  const campStatus = useGetCampaignsOnMount()
  const branchStatus = useGetBranchListOnMount()
  const customFieldsLoading = useGetCustomFieldsOnMount()
  const targetPests = useGetTargetPestsOnMount()
  const allUsersStatus = useGetUserListOnMount()

  const coreDataLoading =
    divStatus === 'pending' ||
    locTypeStatus === 'pending' ||
    funnelStatus === 'pending' ||
    stageStatus === 'pending' ||
    teamStatus === 'pending' ||
    sourceStatus === 'pending' ||
    campStatus === 'pending' ||
    branchStatus === 'pending' ||
    customFieldsLoading ||
    targetPests === 'pending' ||
    allUsersStatus === 'pending'

  useEffect(() => {
    dispatch(setLeadsSearch(''))
    dispatch(setOppsSearch(''))
    dispatch(getGuestUserLeads())
    dispatch(getGuestUserOpportunities())
  }, [dispatch])

  const isLeads = activeTab === 0

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const oppsInitialTotal = useReduxSelector(
    (s: StoreData) => s.guestUser.oppsInitialTotalCount,
  )

  return (
    <Box
      minHeight={'calc(100vh - 200px)'}
      bgcolor={'#F9FAFB'}
      style={{ padding: 0 }}
    >
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          bgcolor: 'white',
          p: 2,
          pb: 0,
        }}
      >
        <CompanyIdentifier variant="inline" />
        <Typography
          variant="body1"
          sx={{ fontSize: '16px', paddingLeft: '16px' }}
        >
          Records Submitted
        </Typography>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label={`Leads`} sx={{ minWidth: 72 }} />
          <Tab
            label={`Opportunities`}
            sx={{ minWidth: 72 }}
            disabled={oppsInitialTotal === 0}
          />
        </Tabs>
      </Box>

      {isLeads ? (
        <GuestUserLeadsTable
          coreDataLoading={coreDataLoading}
          setFiltersDisabled={() => {}}
        />
      ) : (
        <GuestUserOppsTable
          coreDataLoading={coreDataLoading}
          setFiltersDisabled={() => {}}
        />
      )}
    </Box>
  )
}

GuestUserDashboard.displayName = 'GuestUserDashboard'
